// One unified card component for every sales-channel partner. Live
// channels (Jobalots today) and coming-soon channels (BargainFox)
// share the same shell — a collapsible <details> with title + status
// pill in the summary, a credential form in the body, and a nested
// <details> for the vendor sub-account list. Coming-soon mode just
// disables the interactive elements and swaps the status pill for a
// "Coming soon" label.

import { useEffect, useState } from "react";
import { useTranslation } from "../../../i18n";
import { apiFetch } from "../../../api/apiFetch";
import { useReadOnly } from "../../../access/ReadOnly";
import ChannelCredentialsForm from "./ChannelCredentialsForm";
import VendorSubAccountRow from "./VendorSubAccountRow";
import AddVendorForm from "./AddVendorForm";
import type { SalesChannelConfig } from "./types";

interface Props {
    channel: SalesChannelConfig;
    comingSoon?: boolean;
    hint?: string;
    onUpdated: (next: SalesChannelConfig) => void;
}

interface WarehouseOption {
    warehouse: string;
    country: string | null;
}

const ChannelCard = ({ channel, comingSoon = false, hint, onUpdated }: Props) => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const disabled = comingSoon || readOnly;
    const vendors = channel.vendors ?? [];

    // Warehouse list (for the per-vendor dropdown). Only relevant for
    // live channels — coming-soon cards skip the load entirely. The
    // routing decision lives on each vendor row (vendor.defaultWarehouse),
    // not in a separate table — many vendors can target the same
    // warehouse.
    const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);

    useEffect(() => {
        if (comingSoon) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await apiFetch("/settings", {});
                if (!res.ok) return;
                const rows = await res.json();
                if (cancelled) return;
                setWarehouses(
                    (Array.isArray(rows) ? rows : []).map((r) => ({
                        warehouse: r.warehouse,
                        country: r.country ?? null,
                    })),
                );
            } catch { /* silent — vendor rows just show no options */ }
        })();
        return () => { cancelled = true; };
    }, [comingSoon]);

    let pillClass: string;
    let pillLabel: string;
    if (comingSoon) {
        pillClass = "admin-integrations-pill admin-integrations-pill--coming-soon";
        pillLabel = t("integrations_coming_soon");
    } else if (channel.enabled) {
        pillClass = "admin-integrations-status-pill admin-integrations-status-pill--ok";
        pillLabel = t("integrations_status_pill_enabled");
    } else {
        pillClass = "admin-integrations-status-pill admin-integrations-status-pill--off";
        pillLabel = t("integrations_status_pill_disabled");
    }

    return (
        <details
            className={`admin-integrations-card-details${comingSoon ? " admin-integrations-card-details--coming-soon" : ""}`}
            id={channel.channelCode}
        >
            <summary className="admin-integrations-card-summary">
                <span className="admin-integrations-card-summary-title">
                    {channel.displayName}
                </span>
                {!comingSoon && channel.enabled && channel.pushMode === "dry_run" && (
                    <span className="admin-integrations-pill admin-integrations-pill--dry-run">
                        {t("integrations_push_mode_dry_run")}
                    </span>
                )}
                <span className={pillClass}>{pillLabel}</span>
            </summary>

            <div className="admin-integrations-card-body">
                {hint && <p className="admin-integrations-card-hint">{hint}</p>}

                <ChannelCredentialsForm
                    channel={channel}
                    onUpdated={onUpdated}
                    disabled={disabled}
                    warehouses={warehouses}
                />

                <details className="admin-integrations-vendors-details">
                    <summary className="admin-integrations-vendors-summary">
                        <span>{t("integrations_vendors_heading")}</span>
                        <span className="admin-integrations-vendors-count">
                            {vendors.length}
                        </span>
                    </summary>
                    <div className="admin-integrations-vendors-body">
                        <p className="admin-integrations-vendors-hint">
                            {t("integrations_vendors_hint")}
                        </p>
                        {vendors.length === 0 ? (
                            <p className="admin-integrations-vendors-empty">
                                {t("integrations_vendors_empty")}
                            </p>
                        ) : (
                            <div className="admin-integrations-vendors-list">
                                {vendors.map((v) => (
                                    <VendorSubAccountRow
                                        key={v.id}
                                        channelCode={channel.channelCode}
                                        vendor={v}
                                        warehouses={warehouses}
                                        onChannelUpdated={onUpdated}
                                        disabled={disabled}
                                    />
                                ))}
                            </div>
                        )}
                        <AddVendorForm
                            channelCode={channel.channelCode}
                            onCreated={onUpdated}
                            disabled={disabled}
                        />
                    </div>
                </details>
            </div>
        </details>
    );
};

export default ChannelCard;
